import { useMemo } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const publicApi = axios.create({ baseURL: API });

export function useApi() {
  const { getToken } = useAuth();
  return useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use(async (c) => {
      const t = await getToken();
      if (t) c.headers.Authorization = `Bearer ${t}`;
      return c;
    });
    return inst;
  }, [getToken]);
}

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return d || e?.message || "Erreur inattendue";
};

export function useGet(path, params, opts = {}) {
  const api = useApi();
  return useQuery({
    queryKey: [path, params],
    queryFn: async () => (await api.get(path, { params })).data,
    ...opts,
  });
}

export function useSave(invalidate = []) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ method = "post", url, data }) => (await api[method](url, data)).data,
    onSuccess: () => invalidate.forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
    onError: (e) => toast.error(errMsg(e)),
  });
}

export const eur = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(v || 0));

export const fdate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "—");

export const downloadPdf = (api, kind, id) => downloadFile(api, `/pdf/${kind}/${id}`, `${kind}.pdf`);

export async function downloadFile(api, path, fallback) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || fallback;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 3000);
  } catch (e) {
    toast.error("Téléchargement indisponible");
  }
}
